import { Directive, OnInit, ViewChild } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { ContextualSpeedingEditor } from "./contextual-speeding-editor";

@Directive()
export abstract class ContextualSpeedingTab<TItem extends { Id: number }, TEditor extends ContextualSpeedingEditor<TItem>> implements OnInit {
    columns: IVTableColumn<TItem>[] = [];
    rows: IVTableRow<TItem>[] = [];
    items: TItem[] = [];

    loading = false;
    editorVisible = false;

    selectedItem?: TItem;

    @ViewChild('editor') editor: TEditor;

    constructor(public readonly vTableService: VTableService) {}

    async ngOnInit(): Promise<void> {
        this.createTable();
        await this.load();
    }

    async onSaveButtonClicked(): Promise<void> {
        const item = await this.editor.save();
        this.updateData(item)
        this.hideEditor();
    }

    rowClicked(tableRow: IVTableRow<TItem>): void {
        if (!tableRow.data) {
            return;
        }

        this.selectedItem = tableRow.data;
        this.showEditor();
    }

    showEditor(): void {
        this.editorVisible = true;
    }

    hideEditor(): void {
        this.editorVisible = false;
        this.clearSelectedItem();
    }

    clearSelectedItem(): void {
        this.selectedItem = null;
    }

    protected async onConfirmDeleteButton(confirm: boolean): Promise<void> {
        if (!confirm) {
            return;
        }

        await this.deleteItem(this.selectedItem);
        this.deleteSelectedItem();
        this.hideEditor();
    }

    private async load(): Promise<void> {
        this.loading = true;

        this.items = await this.loadData();
        this.rows = this.vTableService.createTableRows(this.items, this.columns)

        this.loading = false;
    }

    private deleteSelectedItem(): void {
        this.items = this.items.filter(x => x.Id !== this.selectedItem.Id);
        this.rows = this.vTableService.createTableRows(this.items, this.columns);
    }

    private updateData(item: TItem): void {
        const itemIndex = this.items.findIndex(x => x.Id === item.Id);

        if (itemIndex !== -1) {
            this.items[itemIndex] = item;
        } else {
            this.items.push(item);
        }

        this.rows = this.vTableService.createTableRows(this.items, this.columns);
    }

    abstract loadData(): Promise<TItem[]>;
    abstract deleteItem(item: TItem): Promise<void>;
    abstract createTable(): void;
}